// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/**
 * Modal + toast infrastructure and the static modal content (How it works,
 * Privacy / threat model, About). The modal host is hidden with the `hidden`
 * attribute and the global `[hidden]{display:none!important}` guard, so no
 * invisible overlay can ever swallow taps.
 */
import { icon } from './icons.ts';

let hostEl: HTMLElement | null = null;
let lastFocus: HTMLElement | null = null;

export function initModals(): void {
  const host = document.createElement('div');
  host.id = 'modal-host';
  host.hidden = true;
  host.innerHTML = `<div class="modal-backdrop" data-close></div><div class="modal" role="dialog" aria-modal="true"></div>`;
  document.body.appendChild(host);
  hostEl = host;
  host.addEventListener('click', (e) => {
    const t = e.target as HTMLElement;
    if (t.dataset.close !== undefined || t.closest('[data-close]')) closeModal();
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && hostEl && !hostEl.hidden) closeModal();
  });
}

export function openModal(title: string, bodyHtml: string): void {
  if (!hostEl) return;
  lastFocus = document.activeElement as HTMLElement;
  const modal = hostEl.querySelector('.modal') as HTMLElement;
  modal.innerHTML = `
    <div class="modal-head">
      <h2 class="modal-title">${escapeHtml(title)}</h2>
      <button type="button" class="icon-btn" data-close aria-label="Close">${icon('x')}</button>
    </div>
    <div class="modal-body">${bodyHtml}</div>`;
  hostEl.hidden = false;
  const close = modal.querySelector('[data-close]') as HTMLElement | null;
  close?.focus();
}

export function closeModal(): void {
  if (!hostEl) return;
  hostEl.hidden = true;
  const modal = hostEl.querySelector('.modal') as HTMLElement;
  modal.innerHTML = '';
  lastFocus?.focus?.();
}

export function isModalOpen(): boolean {
  return !!hostEl && !hostEl.hidden;
}

let toastTimer: ReturnType<typeof setTimeout> | null = null;
export function toast(msg: string, kind: 'info' | 'ok' | 'err' = 'info'): void {
  let el = document.getElementById('toast');
  if (!el) {
    el = document.createElement('div');
    el.id = 'toast';
    document.body.appendChild(el);
  }
  el.textContent = msg;
  el.dataset.kind = kind;
  el.classList.add('show');
  if (toastTimer) clearTimeout(toastTimer);
  toastTimer = setTimeout(() => el?.classList.remove('show'), 2600);
}

function g(term: string, label?: string): string {
  return `<span class="glossary-link" data-term="${escapeHtml(term)}" tabindex="0" role="button">${escapeHtml(label ?? term)}</span>`;
}

export function howItWorksHtml(): string {
  return `
    <ol class="steps">
      <li><span class="step-n">1</span><div><strong>Open Beamwell on both devices.</strong>
        The first device mints a random room id and a four-word ${g('passphrase', 'passphrase')}. Both live only in the link’s <code>#hash</code>, which browsers never send to a server.</div></li>
      <li><span class="step-n">2</span><div><strong>Point one device’s camera at the other’s QR.</strong>
        The ${g('barcode detector', 'camera QR scanner')} reads the pairing code on-device — or just open the link on the other device. Either way, both sides now share the same secret.</div></li>
      <li><span class="step-n">3</span><div><strong>The devices find each other.</strong>
        Each browser finds the other through a public ${g('signalling relay', 'signalling relay')} — used once, for the handshake only — then opens a direct ${g('data channel', 'WebRTC data channel')} with ${g('stun', 'STUN')}. No server sits in the item path.</div></li>
      <li><span class="step-n">4</span><div><strong>Confirm the four words.</strong>
        Both devices show the same ${g('sas', 'four-word code')}. If they match, no impostor slipped into the middle.</div></li>
      <li><span class="step-n">5</span><div><strong>Beam anything.</strong>
        Type or paste text, a link or a one-time code, or drop an image, and press Beam. Each item is sealed with ${g('aes-gcm', 'AES-GCM-256')} before it leaves and opened only on the far side — where you tap to copy it or save it.</div></li>
      <li><span class="step-n">6</span><div><strong>Next time is one tap.</strong>
        Choose to ${g('remembered pairing', 'remember the pairing')} and both devices keep the room and passphrase locally, so reconnecting later takes a single tap. Clear it whenever you like.</div></li>
    </ol>
    <p class="muted small">Camera scanning needs a browser with the BarcodeDetector API (Chrome, Edge, most Android browsers). Everywhere else, open or paste the pairing link — it works identically.</p>`;
}

export function privacyHtml(): string {
  return `
    <div class="tm">
      <section><h3 class="tm-good">Protected</h3><ul>
        <li>The <strong>contents</strong> of everything you beam — text, links, codes, images — travel over a ${g('data channel', 'direct data channel')} between the two browsers, ${g('dtls', 'DTLS')}-encrypted, and are additionally sealed in ${g('aes-gcm', 'AES-GCM-256')} with a key derived from your ${g('passphrase', 'passphrase')}. No server is ever in the item path — the plaintext exists only inside your two tabs.</li>
        <li>The <strong>${g('passphrase', 'passphrase')}</strong> encrypts the ${g('signalling relay', 'signalling')} handshake and derives both the item key and the ${g('sas', 'four verification words')}. It lives only in the link’s hash and never reaches any server.</li>
        <li>The <strong>camera</strong> is used only to read the pairing QR, decoded on-device by the ${g('barcode detector', 'BarcodeDetector')}. Frames are never recorded or uploaded, and the camera is released the moment a code is read.</li>
        <li><strong>Nothing is stored server-side, ever.</strong> No account, no cookies, no third-party fonts, no history in the cloud.</li>
      </ul></section>
      <section><h3 class="tm-bad">Not protected</h3><ul>
        <li>Peer discovery uses <strong>public nostr relays</strong>. They learn an ephemeral room id and that two browsers are connecting, but the handshake is encrypted with your passphrase — they can’t read it or your items.</li>
        <li>Your <strong>IP address</strong> is visible to the device you connect to and to the public ${g('stun', 'STUN')} server — inherent to any peer-to-peer session.</li>
        <li>NAT traversal uses public STUN only. A ${g('symmetric nat', 'symmetric-NAT')} pair with no relay may fail to connect. We won’t add a relay because it would be a backend that could see traffic.</li>
        <li>A <strong>${g('remembered pairing', 'remembered pairing')}</strong> stores the room and passphrase in this browser’s local storage. Anyone with the unlocked device could reuse it — clear it from the app, and don’t remember a pairing on a shared machine.</li>
      </ul></section>
      <section><h3 class="tm-trust">Trust surface</h3><ul>
        <li>The static site bundle (hash-pinned by the GitHub Pages deploy) and the TLS chain to it.</li>
        <li>The public signalling relays and STUN server (encrypted handshake only; they never carry your items).</li>
        <li>A Cloudflare Web Analytics beacon records anonymous page views — no cookies, no fingerprinting, no cross-site tracking; your items are never sent to it.</li>
        <li>Feedback you choose to send goes to feedback.benrichardson.dev. Nothing is sent unless you open the feedback form and press Send; your items are never sent.</li>
      </ul></section>
    </div>`;
}

export function aboutHtml(): string {
  return `
    <p>Beamwell is a private “send this to my phone” button. Pair two of your own devices once — scan a QR or open a link — then beam text, links, one-time codes and images between them instantly, sent directly device-to-device and encrypted end-to-end. No cloud clipboard, no account, no upload. It runs entirely in your browser.</p>
    <p>It’s part of a small collection of privacy-first, zero-backend browser tools. Everything happens on your device.</p>
    <p class="about-links">
      <a href="https://benrichardson.dev/" target="_blank" rel="noopener">benrichardson.dev</a>
      <a href="https://lab.benrichardson.dev" target="_blank" rel="noopener">more tools &amp; sites</a>
      <a href="https://github.com/ben-gy/beamwell" target="_blank" rel="noopener">source on GitHub</a>
    </p>`;
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}
